import axios from "axios";
import ImageWIthSignedUrl from "components/S3Utility/ImageWIthSignedUrl";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import {
  TransformComponent,
  TransformWrapper,
  useControls,
} from "react-zoom-pan-pinch";
import authHeader from "services/auth-header";
import API from "services/axios";
import {
  convertAttendanceRecordToTime,
  convertSqlTimestampToDate,
  formatSqlDate,
} from "utils/UtilityFuncs";
import SendWoApproval from "./SendWoApproval";
import "./WOApproval.css";

export default function WOApproval({
  selectedWO,
  woProducts,
  refreshProducts,
}) {
  const dispatch = useDispatch();
  /*   All States
   ********************************************* */
  const [loading, setLoading] = useState(false);
  const [approvalData, setApprovalData] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showCommentHistoryModal, setShowCommentHistoryModal] = useState(false);
  const [commentHistoryTitle, setCommentHistoryTitle] =
    useState("Comment History");
  const [commentHistoryItems, setCommentHistoryItems] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  /*   All Functions
   ******************************************** */
  const loadingOn = () => {
    setLoading(true);
  };

  const loadingOff = () => {
    setLoading(false);
  };

  const Controls = () => {
    const { zoomIn, zoomOut, resetTransform } = useControls();

    return (
      <div className="image-controls ms-3 d-flex align-items-center gap-2">
        <button
          className="control-btn zoom-btn"
          onClick={() => zoomIn()}
          aria-label="Zoom In"
        >
          <i className="mdi mdi-plus"></i>
        </button>
        <button
          className="control-btn zoom-btn"
          onClick={() => zoomOut()}
          aria-label="Zoom Out"
        >
          <i className="mdi mdi-minus"></i>
        </button>
        <button
          className="control-btn reset-btn"
          onClick={() => resetTransform()}
          aria-label="Reset Zoom"
        >
          <i className="mdi mdi-refresh"></i>
        </button>
      </div>
    );
  };

  const getApprovalData = async () => {
    try {
      loadingOn();
      const { data } = await API.get("/wo-lot-approvals/" + selectedWO.woId, {
        headers: authHeader(),
      });
      if (data?.success) {
        setApprovalData(data.data);
      } else {
        dispatch(setNotification({ type: "error", message: data.message }));
      }
      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  const parsedShippingAddress = useMemo(() => {
    if (!selectedWO?.shippingAddress) return null;
    try {
      // First parse the outer string
      const parsedOuter =
        typeof selectedWO.shippingAddress === "string"
          ? JSON.parse(selectedWO.shippingAddress)
          : selectedWO.shippingAddress;

      // Then parse the inner JSON string
      return typeof parsedOuter === "string"
        ? JSON.parse(parsedOuter)
        : parsedOuter;
    } catch (e) {
      console.error("Error parsing shipping address:", e);
      return null;
    }
  }, [selectedWO]);

  const parsedContactData = useMemo(() => {
    if (!selectedWO?.contactData) return null;
    try {
      return typeof selectedWO.contactData === "string"
        ? JSON.parse(selectedWO.contactData)
        : selectedWO.contactData;
    } catch (e) {
      console.error("Error parsing contact data:", e);
      return null;
    }
  }, [selectedWO]);

  // A staff `by` value looks like "First Last (id:5)" (or "... [via customer
  // link]") -- pull out just the name for display instead of the raw record.
  const getStaffDisplayName = (by) => {
    if (!by) return "Staff";
    const match = by.match(/^(.*?)\s*\(id:/);
    const name = (match ? match[1] : by).trim();
    return name || "Staff";
  };

  // Whether the whole approval was actioned by the customer, staff, or both
  const approvedByLabel = useMemo(() => {
    const entries = [
      {
        byType: approvalData?.excelApprovedByType,
        by: approvalData?.excelApprovedBy,
      },
      {
        byType: approvalData?.shippingApprovedByType,
        by: approvalData?.shippingApprovedBy,
      },
      {
        byType: approvalData?.inHandByApprovedByType,
        by: approvalData?.inHandByApprovedBy,
      },
      ...(woProducts?.map((p) => ({
        byType: p.approvedByType,
        by: p.approvedBy,
      })) || []),
    ].filter((e) => e.byType);

    if (entries.length === 0) return "-";
    const hasCustomer = entries.some((e) => e.byType === "customer");
    const staffEntries = entries.filter((e) => e.byType !== "customer");
    const hasStaff = staffEntries.length > 0;

    if (hasCustomer && hasStaff) return "Mixed (Customer & Staff)";
    if (hasCustomer) return "Customer";

    const staffNames = [
      ...new Set(staffEntries.map((e) => getStaffDisplayName(e.by))),
    ];
    return staffNames.length === 1 ? staffNames[0] : "Staff";
  }, [
    approvalData?.excelApprovedByType,
    approvalData?.excelApprovedBy,
    approvalData?.shippingApprovedByType,
    approvalData?.shippingApprovedBy,
    approvalData?.inHandByApprovedByType,
    approvalData?.inHandByApprovedBy,
    woProducts,
  ]);

  // parsed billing address
  const parsedBillingAddress = useMemo(() => {
    if (!selectedWO?.billingAddress) return null;
    try {
      // First parse the outer string
      const parsedOuter =
        typeof selectedWO.billingAddress === "string"
          ? JSON.parse(selectedWO.billingAddress)
          : selectedWO.billingAddress;

      // Then parse the inner JSON string
      return typeof parsedOuter === "string"
        ? JSON.parse(parsedOuter)
        : parsedOuter;
    } catch (e) {
      console.error("Error parsing billing address:", e);
      return null;
    }
  }, [selectedWO]);

  const ShippingAddress = () => {
    if (!parsedShippingAddress) return <div>No shipping address available</div>;
    return (
      <>
        <div className="row">
          <div className="col-4 fw-bold"> Company</div>
          <div className="col-8">{parsedShippingAddress?.addressCompany}</div>
        </div>
        <div className="row">
          <div className="col-4 fw-bold">Address Line 1</div>
          <div className="col-8">{parsedShippingAddress?.addressLine1}</div>
        </div>
        <div className="row">
          <div className="col-4 fw-bold">Address Line 2</div>
          <div className="col-8">{parsedShippingAddress?.addressLine2}</div>
        </div>
        <div className="row">
          <div className="col-4 fw-bold">City | State | Zip</div>
          <div className="col-8">
            {parsedShippingAddress?.city} | {parsedShippingAddress?.state} |{" "}
            {parsedShippingAddress?.zip}
          </div>
        </div>
        <div className="row">
          <div className="col-4 fw-bold">Country</div>
          <div className="col-8">{parsedShippingAddress?.country}</div>
        </div>
      </>
    );
  };

  const BillingAddress = () => {
    if (!parsedBillingAddress) return <div>No billing address available</div>;
    return (
      <>
        <div className="row">
          <div className="col-4 fw-bold"> Company</div>
          <div className="col-8">{parsedBillingAddress?.addressCompany}</div>
        </div>
        <div className="row">
          <div className="col-4 fw-bold">Address Line 1</div>
          <div className="col-8">{parsedBillingAddress?.addressLine1}</div>
        </div>
        <div className="row">
          <div className="col-4 fw-bold">Address Line 2</div>
          <div className="col-8">{parsedBillingAddress?.addressLine2}</div>
        </div>
        <div className="row">
          <div className="col-4 fw-bold">City | State | Zip</div>
          <div className="col-8">
            {parsedBillingAddress?.city} | {parsedBillingAddress?.state} |{" "}
            {parsedBillingAddress?.zip}
          </div>
        </div>
        <div className="row">
          <div className="col-4 fw-bold">Country</div>
          <div className="col-8">{parsedBillingAddress?.country}</div>
        </div>
      </>
    );
  };

  const downloadFile = (fileKey) => {
    axios
      .post(`${process.env.REACT_APP_API_URI}/aws/images`, {
        key: fileKey,
      })
      .then((response) => {
        const url = response.data[0];

        const link = document.createElement("a");
        link.href = url;
        // Opens in a new tab (user, 2026-11-02) instead of downloading.
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch((error) => {
        console.log("Error downloading file:", error);
        dispatch(
          setNotification({
            type: "error",
            message: "Failed to download image.",
          }),
        );
      });
  };

  const checkProductsApprovalStatus = (product) => {
    return product.approveStatus === "approved";
  };

  const resetApprovals = async ({ approvalId, woId }) => {
    try {
      setLoading(true);
      const { data } = await API.post(
        "/wo-lot-approvals-reset-all",
        {
          approvalId,
          woId,
        },
        { headers: authHeader() },
      );
      if (data?.success) {
        await getApprovalData();
        await refreshProducts();
        dispatch(
          setNotification({
            message: "All approvals have been reset.",
            type: "success",
          }),
        );
      } else {
        dispatch(
          setNotification({
            message: "Failed to reset approvals.",
            type: "error",
          }),
        );
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  // Reset All Rejected Approval Status
  const resetAllRejectedApprovalStatus = async () => {
    try {
      setLoading(true);
      const { data } = await API.post(
        "/wo-lot-approvals-reset-rejected",
        {
          woId: selectedWO.woId,
          approvalId: approvalData?.approvalId,
        },
        {
          headers: authHeader(),
        },
      );
      if (data?.success) {
        await getApprovalData();
        await refreshProducts();
        dispatch(
          setNotification({
            message: data?.message,
            type: "success",
          }),
        );
      } else {
        dispatch(
          setNotification({
            message: data?.message,
            type: "error",
          }),
        );
      }
      setLoading(false);
    } catch (err) {
      console.log(err.message);
      setLoading(false);
    }
  };

  const resetApprovalStatus = async (type, productId) => {
    try {
      setLoading(true);
      const { data } = await API.post(
        `/wo-lot-approval-reset-status`,
        { type, woId: selectedWO.woId, productId },
        {
          headers: authHeader(),
        },
      );
      if (data?.success) {
        await getApprovalData();
        if (type === "product") {
          await refreshProducts();
        }
        await getApprovalData();

        dispatch(
          setNotification({
            message: data?.message,
            type: "success",
          }),
        );
      } else {
        dispatch(
          setNotification({
            message: data?.message,
            type: "error",
          }),
        );
      }
      setLoading(false);
    } catch (err) {
      console.log(err.message);
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  useEffect(() => {
    let timeout = setTimeout(() => {
      // console.log("selectedWO", selectedWO);
      // console.log("woProducts", woProducts);
      let timeout = setTimeout(() => {
        if (selectedWO && woProducts) {
          getApprovalData();
        }
      }, 50);
      return () => clearTimeout(timeout);
    }, 50);
    return () => clearTimeout(timeout);
  }, [selectedWO, selectedWO?.customerId, woProducts]);

  /*   Helper Functions
   ********************************************* */
  const normalizeApprovalStatus = (status) =>
    status === "approved_with_changes" ? "approved" : status;

  const formatApprovalStatusLabel = (status) =>
    (normalizeApprovalStatus(status) || "").replaceAll("_", " ").toUpperCase();

  const REJECTED_STATUS_ORANGE = "#f97316";
  const ORANGE_BUTTON_STYLE = {
    backgroundColor: REJECTED_STATUS_ORANGE,
    borderColor: REJECTED_STATUS_ORANGE,
    color: "#fff",
  };

  const getRejectedStatusStyle = (status) =>
    normalizeApprovalStatus(status) === "rejected"
      ? { backgroundColor: REJECTED_STATUS_ORANGE, color: "#fff" }
      : undefined;

  const getRejectedStatusSoftBgStyle = (status) =>
    normalizeApprovalStatus(status) === "rejected"
      ? { backgroundColor: "#ffedd5" }
      : undefined;

  const getApprovalStatusBadge = (status) => {
    switch (normalizeApprovalStatus(status)) {
      case "approved":
        return "badge bg-success";
      case "rejected":
        return "badge";
      default:
        return "badge bg-warning";
    }
  };

  // Small "Staff name" / "Customer" badge next to a section's approval status.
  // `size="lg"` matches the fs-6 py-1 px-2 lh-1 status pills used in the section
  // headers; `size="sm"` matches the plain default-size badge used per-product.
  const ApprovedByBadge = ({ byType, by, size = "lg", className = "" }) => {
    if (!byType) return null;
    const isCustomer = byType === "customer";
    const label = isCustomer ? "Customer" : getStaffDisplayName(by);
    return (
      <span
        className={`badge ${isCustomer ? "bg-info" : "bg-secondary"} ${
          size === "lg" ? "fs-6 py-1 px-2 lh-1" : ""
        } ${className} ms-1`}
        title={isCustomer ? "Approved by Customer" : `Approved by ${label}`}
      >
        {label}
      </span>
    );
  };

  const handleImageDoubleClick = (imagePath, productDescription) => {
    setSelectedImage({ path: imagePath, alt: productDescription });
    setShowImageModal(true);
  };

  const parseCommentHistory = (rawComment) => {
    if (!rawComment) return [];

    const normalizeEntry = (entry, index = 0) => {
      if (!entry) return null;

      if (typeof entry === "string") {
        const trimmed = entry.trim();
        if (!trimmed) return null;
        return {
          comment: trimmed,
          timestamp: null,
          sortIndex: index,
        };
      }

      if (typeof entry !== "object") return null;

      const commentText =
        entry.Comments ?? entry.comments ?? entry.comment ?? entry.message;
      if (typeof commentText !== "string" || !commentText.trim()) return null;

      const timestampValue =
        entry.timestamp ?? entry.time ?? entry.createdAt ?? entry.date;
      const parsedTimestamp = timestampValue ? Date.parse(timestampValue) : NaN;

      return {
        comment: commentText.trim(),
        timestamp: Number.isNaN(parsedTimestamp)
          ? null
          : new Date(parsedTimestamp).toISOString(),
        sortIndex: index,
      };
    };

    const normalizeAndSort = (entries) => {
      const validEntries = entries.filter(Boolean);
      if (!validEntries.length) return [];

      const hasTimestamp = validEntries.some((item) => item.timestamp);
      if (!hasTimestamp) return validEntries;

      return [...validEntries].sort((a, b) => {
        const ta = a.timestamp ? Date.parse(a.timestamp) : -Infinity;
        const tb = b.timestamp ? Date.parse(b.timestamp) : -Infinity;
        if (ta === tb) return a.sortIndex - b.sortIndex;
        return ta - tb;
      });
    };

    if (typeof rawComment === "string") {
      const trimmed = rawComment.trim();
      if (!trimmed) return [];

      if (
        (trimmed.startsWith("[") && trimmed.endsWith("]")) ||
        (trimmed.startsWith("{") && trimmed.endsWith("}"))
      ) {
        try {
          const parsed = JSON.parse(trimmed);

          if (Array.isArray(parsed)) {
            return normalizeAndSort(
              parsed.map((entry, index) => normalizeEntry(entry, index)),
            );
          }

          return normalizeAndSort([normalizeEntry(parsed, 0)]);
        } catch (err) {
          return normalizeAndSort([normalizeEntry(trimmed, 0)]);
        }
      }

      return normalizeAndSort([normalizeEntry(trimmed, 0)]);
    }

    if (Array.isArray(rawComment)) {
      return normalizeAndSort(
        rawComment.map((entry, index) => normalizeEntry(entry, index)),
      );
    }

    return normalizeAndSort([normalizeEntry(rawComment, 0)]);
  };

  const getLatestComment = (rawComment) => {
    const history = parseCommentHistory(rawComment);
    return history.length ? history[history.length - 1].comment : "";
  };

  const formatHistoryTimestamp = (timestamp) => {
    if (!timestamp) return "No timestamp";
    const parsed = Date.parse(timestamp);
    if (Number.isNaN(parsed)) return "No timestamp";
    return new Date(parsed).toLocaleString();
  };

  const openCommentHistory = (title, rawComment) => {
    const history = parseCommentHistory(rawComment);
    setCommentHistoryTitle(title);
    setCommentHistoryItems(history);
    setShowCommentHistoryModal(true);
  };

  const excelCommentHistory = useMemo(
    () => parseCommentHistory(approvalData?.excelComments),
    [approvalData?.excelComments],
  );

  const latestExcelComment = useMemo(
    () =>
      excelCommentHistory.length
        ? excelCommentHistory[excelCommentHistory.length - 1].comment
        : "",
    [excelCommentHistory],
  );

  // Billing has no "new address" counterpart -- only shipping ever had a
  // newShippingAddress column -- so this is the comments column alone.
  const billingCommentRaw = approvalData?.billingAddressComments;

  const billingCommentHistory = useMemo(
    () => parseCommentHistory(billingCommentRaw),
    [billingCommentRaw],
  );

  const latestBillingComment = useMemo(
    () =>
      billingCommentHistory.length
        ? billingCommentHistory[billingCommentHistory.length - 1].comment
        : "",
    [billingCommentHistory],
  );

  const shippingCommentRaw =
    approvalData?.shippingAddressComments || approvalData?.newShippingAddress;

  const shippingCommentHistory = useMemo(
    () => parseCommentHistory(shippingCommentRaw),
    [shippingCommentRaw],
  );

  const latestShippingComment = useMemo(
    () =>
      shippingCommentHistory.length
        ? shippingCommentHistory[shippingCommentHistory.length - 1].comment
        : "",
    [shippingCommentHistory],
  );

  const handleCloseModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  // The scrollable body used to be a hardcoded 64vh, which assumed a fixed
  // amount of chrome above it. The summary strip is now two rows instead of
  // one, so that height overran the viewport and the last products were cut
  // off. Measure what's actually left below the strip instead, so the panel
  // stays correct however the header and cards wrap.
  const scrollAreaRef = useRef(null);
  const [scrollAreaHeight, setScrollAreaHeight] = useState("64vh");

  useEffect(() => {
    const recompute = () => {
      const el = scrollAreaRef.current;
      if (!el) return;
      const BOTTOM_GUTTER = 16;
      const available =
        window.innerHeight - el.getBoundingClientRect().top - BOTTOM_GUTTER;
      // Floor it so the panel never collapses on a short window.
      setScrollAreaHeight(`${Math.max(available, 240)}px`);
    };

    recompute();
    window.addEventListener("resize", recompute);
    return () => window.removeEventListener("resize", recompute);
    // Re-measure once the content above has rendered -- the cards only reach
    // their real height after approvalData lands.
  }, [approvalData, woProducts, selectedWO]);

  /*   Render Functions
   ********************************************* */
  useEffect(() => {
    let timeout = setTimeout(() => {
      refreshProducts();
    }, 50);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <div className="p-2">
      <style>{`
        .wo-approval-orange-btn {
          background-color: #f97316 !important;
          border-color: #f97316 !important;
          color: #fff !important;
        }
      `}</style>

      {loading && <Spinner />}

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h5 className="mb-0">Approval Summary - WO #{selectedWO?.woId}</h5>
        <div className="d-flex align-items-center gap-1 flex-wrap">
          <button
            onClick={() => setShowDetailsModal(true)}
            className="btn btn-sm btn-warning text-nowrap"
          >
            Details
          </button>

          <button
            onClick={(e) => {
              resetApprovals({
                approvalId: approvalData?.approvalId,
                woId: approvalData?.woId,
              });
            }}
            className="btn btn-sm text-nowrap"
            style={ORANGE_BUTTON_STYLE}
          >
            Reset All Approval Status
          </button>

          {approvalData?.approvalLink && (
            <Link
              to={`/work-order/approvals/${approvalData?.companyId}/${approvalData?.approvalLink}?viewOnly=true`}
              className="btn btn-sm btn-info"
              target="_blank"
            >
              View Approval
            </Link>
          )}

          {/* The customer's own link -- the exact URL emailed to them, which
              is this same page WITHOUT ?viewOnly=true.

              That difference is the whole point and it is not cosmetic. The
              API treats viewOnly / isView as "a staff member is peeking" and
              SKIPS the open tracking; without it the request counts as a real
              customer open and bumps Times Opened / Last Opened -- the two
              stats shown on this very page. So this button is live: it opens
              what the customer opens, and is recorded as such. "View Approval"
              above stays the read-only peek that leaves the stats alone. */}
          {approvalData?.approvalLink && (
            <Link
              to={`/work-order/approvals/${approvalData?.companyId}/${approvalData?.approvalLink}`}
              className="btn btn-sm btn-secondary text-white"
              target="_blank"
              rel="noopener noreferrer"
              title="Opens the live customer page. Counts as a customer open."
            >
              View Customer Link
            </Link>
          )}
          {woProducts && (
            <SendWoApproval
              selectedWO={selectedWO}
              woProducts={woProducts}
              approvalData={approvalData}
              refreshFunc={async () => {
                await getApprovalData();
                refreshProducts();
              }}
              btnClass={"btn btn-primary btn-sm"}
              btnTitle={"Send Approval"}
              customerApproved={!!approvalData?.allApproved}
            />
          )}
        </div>
      </div>

      {/* Summary Stats — 4 per row on desktop, so the 7 cards sit on two rows */}
      <div className="row g-2 mb-1 wo-approval-stat-cards">
        <div className="col-6 col-md-4 col-lg-3">
          <div className="card border bg-white shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between gap-1">
                <span className="fw-bolder text-nowrap">Created At:</span>
                <span className="text-end">
                  {selectedWO?.createdAt
                    ? `${convertSqlTimestampToDate(
                        selectedWO?.createdAt,
                        true,
                      )} - ${convertAttendanceRecordToTime(
                        selectedWO?.createdAt,
                      )}`
                    : "-"}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-4 col-lg-3">
          <div className="card border bg-white shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between gap-1">
                <span className="fw-bolder text-nowrap">Sent Time:</span>
                <span className="text-end">
                  {approvalData?.approvalSentTime
                    ? `${convertSqlTimestampToDate(
                        approvalData?.approvalSentTime,
                        true,
                      )} - ${convertAttendanceRecordToTime(
                        approvalData?.approvalSentTime,
                      )}`
                    : "Not sent"}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-4 col-lg-3">
          <div className="card border bg-white shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between gap-1">
                <span className="fw-bolder text-nowrap">Sent By:</span>
                <span className="text-end">
                  {approvalData?.approvalSentTime
                    ? `${approvalData?.user?.firstName || ""} ${
                        approvalData?.user?.lastName
                      }`
                    : "Not Sent"}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-4 col-lg-3">
          <div className="card border bg-white shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between gap-1">
                <span className="fw-bolder text-nowrap">Last Opened:</span>
                <span className="text-end">
                  {approvalData?.lastOpened
                    ? `${convertSqlTimestampToDate(
                        approvalData?.lastOpened,
                        true,
                      )} - ${convertAttendanceRecordToTime(
                        approvalData?.lastOpened,
                      )}`
                    : approvalData?.approvalSentTime
                      ? "No Opened"
                      : "Not sent"}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-4 col-lg-3">
          <div className="card border bg-white shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between gap-1">
                <span className="fw-bolder text-nowrap">Times Opened:</span>
                <strong className="text-end">
                  {approvalData?.timesOpened || 0}
                </strong>
              </div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-4 col-lg-3">
          <div className="card border bg-white shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between gap-1">
                <span className="fw-bolder text-nowrap">Approved Time:</span>
                <span className="text-end">
                  {approvalData?.approveTime
                    ? `${convertSqlTimestampToDate(
                        approvalData?.approveTime,
                        true,
                      )} - ${convertAttendanceRecordToTime(
                        approvalData?.approveTime,
                      )}`
                    : "Not approved"}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-4 col-lg-3">
          <div className="card border bg-white shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between gap-1">
                <span className="fw-bolder text-nowrap">Approved By:</span>
                <span className="text-end">{approvedByLabel}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        ref={scrollAreaRef}
        style={{
          height: scrollAreaHeight,
          overflowY: "auto",
          overflowX: "hidden",
        }}
        className="pe-2"
      >
        {/* Contact & Shipping */}
        <div className="row mt-1 g-2 wo-approval-info-cards">
            {/* Contact */}
            <div className="col-md-3 mb-1">
              <div className="card border h-100">
                <div className="card-header py-2 bg-light fw-bold">Contact</div>
                <div className="card-body py-2">
                  {parsedContactData ? (
                    <>
                      <div className="row mb-1 gx-1">
                        <div className="col-3 fw-bold">Name:</div>
                        <div className="col-9">
                          {parsedContactData?.firstName}{" "}
                          {parsedContactData?.lastName}
                        </div>
                      </div>
                      <div className="row mb-1 gx-1">
                        <div className="col-3 fw-bold">Email:</div>
                        <div className="col-9">
                          {parsedContactData?.email || "-"}
                        </div>
                      </div>
                      <div className="row mb-1 gx-1">
                        <div className="col-3 fw-bold">Phone:</div>
                        <div className="col-9">
                          {parsedContactData?.phone || "-"}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div>No contact details available</div>
                  )}
                </div>
              </div>
            </div>

            {/* Billing Address */}
            <div className="col-md-3 mb-1">
              <div className="card border h-100">
                <div className="card-header py-2 bg-light fw-bold">
                  Billing Address
                </div>
                <div className="card-body py-2">
                  <BillingAddress />

                  {/* Only when there is something to say. Billing comments
                      exist solely because a customer edited the address from
                      their approval link, so on most orders this whole block
                      is absent rather than an empty "Comments:" heading. */}
                  {billingCommentHistory.length > 0 && (
                    <>
                      <div className="d-flex align-items-center justify-content-between mt-2">
                        <h5 className="fw-bold mb-0">Comments:</h5>
                        <button
                          title="View Comment History"
                          onClick={() =>
                            openCommentHistory(
                              "Billing Address Comments",
                              billingCommentRaw,
                            )
                          }
                          className="btn btn-outline-secondary btn-sm py-0 px-1"
                        >
                          <i className="mdi mdi-comment-text-multiple-outline"></i>
                        </button>
                      </div>
                      <div style={{ whiteSpace: "pre-wrap" }}>
                        {latestBillingComment}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="col-md-3 mb-1">
              {approvalData && approvalData?.shippingAddressStatus ? (
                <div className="card mb-0 border h-100">
                  <div
                    className={`card-header py-2 ${
                      approvalData.shippingAddressStatus === "rejected"
                        ? ""
                        : "bg-success bg-opacity-10"
                    }`}
                    style={getRejectedStatusSoftBgStyle(
                      approvalData.shippingAddressStatus,
                    )}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">Shipping Address</h5>
                      <div>
                        <button
                          title="Reset Status"
                          onClick={() =>
                            resetApprovalStatus("shipping_address")
                          }
                          className="btn px-2 rounded-3 py-1 me-1 fs-6 lh-1"
                          style={ORANGE_BUTTON_STYLE}
                        >
                          R
                        </button>
                        <span
                          className={`${getApprovalStatusBadge(
                            approvalData.shippingAddressStatus,
                          )} fs-6 py-1 px-2 lh-1`}
                          style={getRejectedStatusStyle(
                            approvalData.shippingAddressStatus,
                          )}
                        >
                          {formatApprovalStatusLabel(
                            approvalData.shippingAddressStatus,
                          )}
                        </span>
                        <ApprovedByBadge
                          byType={approvalData?.shippingApprovedByType}
                          by={approvalData?.shippingApprovedBy}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="card-body py-2">
                    <ShippingAddress />
                    <div className="d-flex align-items-center justify-content-between mt-2">
                      <h5 className="fw-bold mb-0">Comments:</h5>
                      {shippingCommentHistory.length > 0 && (
                        <button
                          title="View Comment History"
                          onClick={() =>
                            openCommentHistory(
                              "Shipping Address Comments",
                              shippingCommentRaw,
                            )
                          }
                          className="btn btn-outline-secondary btn-sm py-0 px-1"
                        >
                          <i className="mdi mdi-comment-text-multiple-outline"></i>
                        </button>
                      )}
                    </div>
                    <p>
                      {latestShippingComment
                        ? latestShippingComment
                        : approvalData.shippingAddressStatus ===
                            "pending_approval"
                          ? "Pending Approval"
                          : "No details available"}
                    </p>
                    <hr />
                    <div className="d-flex align-items-center gap-2">
                      <h5 className="fw-bold my-0">Due Date:</h5>
                      <span className="d-inline-flex align-items-center">
                        {approvalData?.inHandByDate ? (
                          formatSqlDate({
                            sqlDate: approvalData?.inHandByDate,
                            yearFormat: "numeric",
                            monthFormat: "long",
                            dayFormat: "2-digit",
                          })
                        ) : (
                          <span className="badge bg-warning py-1">
                            Pending
                          </span>
                        )}
                      </span>
                      <button
                        title="Reset Status"
                        onClick={() => resetApprovalStatus("due_date")}
                        className="btn rounded-3 fs-6 d-inline-flex align-items-center justify-content-center lh-1"
                        style={{
                          ...ORANGE_BUTTON_STYLE,
                          width: 24,
                          height: 24,
                          padding: 0,
                        }}
                      >
                        R
                      </button>
                      <ApprovedByBadge
                        byType={approvalData?.inHandByApprovedByType}
                        by={approvalData?.inHandByApprovedBy}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="card mb-0 border h-100">
                  <div className="card-header py-2 fw-bold bg-light">
                    Shipping Address
                  </div>
                  <div className="card-body py-2">
                    {parsedShippingAddress ? (
                      <ShippingAddress />
                    ) : (
                      <div>No shipping address available</div>
                    )}
                    <hr />
                    <div className="d-flex align-items-center gap-2">
                      <h5 className="fw-bold my-0">Due Date:</h5>
                      <span className="d-inline-flex align-items-center">
                        {approvalData?.inHandByDate ? (
                          convertSqlTimestampToDate(approvalData?.inHandByDate)
                        ) : (
                          <span className="badge bg-warning py-1">
                            Pending
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

          {/* Numbering Sheet */}
          {approvalData?.approvalExcelFile ? (
            <div
              className="col-md-3 mb-1"
              style={
                {
                  //  height: 180
                }
              }
            >
              <div className="card border h-100">
                <div
                  className={`card-header py-2 ${
                    approvalData.excelFileStatus === "rejected"
                      ? ""
                      : "bg-info bg-opacity-10"
                  }`}
                  style={getRejectedStatusSoftBgStyle(
                    approvalData.excelFileStatus,
                  )}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Player Numbering Sheet</h5>
                    <div className="d-flex align-items-center gap-1">
                      <button
                        title="Reset Status"
                        onClick={() => resetApprovalStatus("excel_file")}
                        className="btn px-2 rounded-3 py-1 me-1 fs-6 lh-1"
                        style={ORANGE_BUTTON_STYLE}
                      >
                        R
                      </button>
                      <span
                        className={`${getApprovalStatusBadge(
                          approvalData.excelFileStatus,
                        )} fs-6 py-1 px-2 lh-1`}
                        style={getRejectedStatusStyle(
                          approvalData.excelFileStatus,
                        )}
                      >
                        {formatApprovalStatusLabel(
                          approvalData.excelFileStatus,
                        )}
                      </span>
                      <ApprovedByBadge
                        byType={approvalData?.excelApprovedByType}
                        by={approvalData?.excelApprovedBy}
                      />
                    </div>
                  </div>
                </div>
                <div
                  className="card-body"
                  style={
                    {
                      // height: 150,
                      // overflowY: "auto",
                    }
                  }
                >
                  <div className="row">
                    <div className="col-md-8 border-end">
                      <h5>Player Numbering Sheet:</h5>
                      <div>
                        {approvalData?.approvalExcelFile
                          ? approvalData?.approvalExcelFile?.split("/")?.pop()
                          : "No file available"}
                      </div>
                      <button
                        onClick={() =>
                          downloadFile(approvalData?.approvalExcelFile)
                        }
                        className="btn btn-primary btn-sm mt-2"
                      >
                        Download
                      </button>

                      {approvalData?.customerExcelFile && (
                        <>
                          <hr className="my-3" />
                          <h5>Customer Uploaded Excel:</h5>
                          <div>
                            {approvalData?.customerExcelFile?.split("/")?.pop()}{" "}
                            <br />
                            {`${convertSqlTimestampToDate(approvalData?.customerExcelFileDate)} - ${convertAttendanceRecordToTime(
                              approvalData?.customerExcelFileDate,
                            )}`}
                          </div>
                          <button
                            onClick={() =>
                              downloadFile(approvalData?.customerExcelFile)
                            }
                            className="btn btn-outline-primary btn-sm mt-2"
                          >
                            Download Customer File
                          </button>
                        </>
                      )}
                    </div>
                    <div className="col-md-4">
                      <div className="d-flex align-items-center justify-content-between">
                        <h5 className="fw-bold mb-0">Comments:</h5>
                        {excelCommentHistory.length > 0 && (
                          <button
                            title="View Comment History"
                            onClick={() =>
                              openCommentHistory(
                                "Player Numbering Sheet Comments",
                                approvalData?.excelComments,
                              )
                            }
                            className="btn btn-outline-secondary btn-sm py-0 px-1"
                          >
                            <i className="mdi mdi-comment-text-multiple-outline"></i>
                          </button>
                        )}
                      </div>
                      <p>
                        {latestExcelComment ? (
                          <>{latestExcelComment}</>
                        ) : approvalData.excelFileStatus ===
                          "pending_approval" ? (
                          <>Pending Approval</>
                        ) : (
                          <>No comments available</>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div
              className="col-md-3 mb-1"
              style={
                {
                  // maxHeight: 320,
                }
              }
            >
              <div className="card border h-100">
                <div className="">
                  <div className={`card-header py-2 bg-light`}>
                    Player Numbering Sheet
                  </div>
                </div>

                <div
                  className="card-body py-1"
                  style={
                    {
                      // height: 150,
                      // overflowY: "auto",
                    }
                  }
                >
                  {approvalData?.customerExcelFile && (
                    <div className="mb-2 border rounded p-2">
                      <h6 className="mb-1">Customer Uploaded Excel:</h6>
                      <div className="small text-muted mb-2">
                        {approvalData?.customerExcelFile?.split("/")?.pop()}
                      </div>
                      <button
                        onClick={() =>
                          downloadFile(approvalData?.customerExcelFile)
                        }
                        className="btn btn-outline-primary btn-sm"
                      >
                        Download Customer File
                      </button>
                    </div>
                  )}

                  {/* Upload lives on the W-Attachments tab now; this tab is
                      view-only for the numbering sheet (download + status). */}
                  <div className="text-muted small py-2">
                    No Player Numbering Sheet uploaded yet. Upload it from the{" "}
                    <strong>W-Attachments</strong> tab.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Products List */}
        {woProducts && woProducts.length > 0 ? (
          <div className="card mt-3">
            {/* Info Badges */}
            <div className="card-header py-2">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <h4 className="">Products</h4>
                  <span
                    className={`h6 text-white rounded-pill flex-center px-2 py-1 ${
                      woProducts?.length > 0 &&
                      woProducts?.every((p) => p.approveStatus === "approved")
                        ? "bg-success"
                        : ""
                    }`}
                    style={
                      woProducts?.length > 0 &&
                      woProducts?.every((p) => p.approveStatus === "approved")
                        ? undefined
                        : { backgroundColor: REJECTED_STATUS_ORANGE }
                    }
                  >
                    All Approved:{" "}
                    {woProducts?.length > 0 &&
                    woProducts?.every((p) => p.approveStatus === "approved")
                      ? "Yes"
                      : "No"}
                  </span>
                  {approvalData?.approvalExcelFile && (
                    <span
                      className={`h6 rounded-pill flex-center px-2 py-1 ${
                        approvalData.excelFileStatus === "approved"
                          ? "bg-success text-white "
                          : approvalData.excelFileStatus === "rejected"
                            ? "text-white"
                            : "bg-warning text-black"
                      }`}
                      style={
                        approvalData.excelFileStatus === "rejected"
                          ? { backgroundColor: REJECTED_STATUS_ORANGE }
                          : undefined
                      }
                    >
                      Excel:{" "}
                      {approvalData.excelFileStatus === "approved"
                        ? "Approved"
                        : approvalData.excelFileStatus === "rejected"
                          ? "Rejected"
                          : "Pending"}
                    </span>
                  )}

                  {approvalData?.shippingAddressStatus &&
                    approvalData.shippingAddressStatus !==
                      "pending_approval" && (
                      <span
                        className={`h6 rounded-pill flex-center px-2 py-1 ${
                          approvalData.shippingAddressStatus === "approved"
                            ? "bg-success text-white "
                            : "text-white "
                        }`}
                        style={
                          approvalData.shippingAddressStatus === "rejected"
                            ? { backgroundColor: REJECTED_STATUS_ORANGE }
                            : undefined
                        }
                      >
                        Shipping:{" "}
                        {approvalData.shippingAddressStatus === "approved"
                          ? "Approved"
                          : "Rejected"}
                      </span>
                    )}
                </div>
                <div className="d-flex align-items-center  gap-2">
                  <span className="h5">
                    <strong>Total: {woProducts?.length || 0}</strong>
                  </span>
                  <span className="h6 text-white rounded-pill flex-center px-2 py-1 bg-success">
                    Approved:{" "}
                    {woProducts?.filter((p) => p.approveStatus === "approved")
                      .length || 0}
                  </span>
                  <span
                    className="h6 text-white rounded-pill flex-center px-2 py-1"
                    style={{ backgroundColor: REJECTED_STATUS_ORANGE }}
                  >
                    Rejected:{" "}
                    {woProducts?.filter((p) => p.approveStatus === "rejected")
                      .length || 0}
                  </span>
                  <span className="h6 text-white rounded-pill flex-center px-2 py-1 bg-warning">
                    Pending:{" "}
                    {woProducts?.filter((p) => p.approveStatus === "pending")
                      .length || 0}
                  </span>
                </div>
              </div>
            </div>

            {/* Product Details list */}
            <div className="card-body p-0">
              <div className="d-flex align-items-center gap-2 bg-light py-2 px-3 border-bottom">
                <div style={{ flex: "0 0 32px", textAlign: "center" }}>
                  <strong>#</strong>
                </div>
                <div style={{ flex: "0 0 12%" }}>
                  <strong>Style</strong>
                </div>
                <div style={{ flex: "0 0 18%" }}>
                  <strong>Type</strong>
                </div>
                <div style={{ flex: "0 0 16%" }}>
                  <strong>Desc</strong>
                </div>
                <div style={{ flex: "0 0 10%" }}>
                  <strong>Color</strong>
                </div>
                <div style={{ flex: "1 1 auto" }}>
                  <strong>Comments</strong>
                </div>
                <div style={{ flex: "0 0 260px" }}>
                  <strong>Status</strong>
                </div>
              </div>
              <div>
                {/* Display products */}
                {woProducts.map((product, index) =>
                  (() => {
                    const productCommentHistory = parseCommentHistory(
                      product.approvalComments,
                    );
                    const latestProductComment = getLatestComment(
                      product.approvalComments,
                    );

                    return (
                      <div
                        key={product.id}
                        className={`py-2 px-3 ${
                          index !== woProducts.length - 1 ? "border-bottom" : ""
                        }`}
                      >
                        <div className="d-flex align-items-center gap-2">
                          {/* # -- plain product number (matches the customer
                              approval card / WO PDF numbering). */}
                          <div
                            className="fw-semibold text-center"
                            style={{ flex: "0 0 32px" }}
                          >
                            {index + 1}
                          </div>
                          {/* Style */}
                          <div
                            className="text-break"
                            style={{ flex: "0 0 12%" }}
                          >
                            {product.productStyle || (
                              <span className="text-muted">-</span>
                            )}
                          </div>
                          {/* Type */}
                          <div
                            className="text-break"
                            style={{ flex: "0 0 18%" }}
                          >
                            {product.productTypes?.productTypes || (
                              <span className="text-muted">-</span>
                            )}
                          </div>
                          {/* Desc */}
                          <div
                            className="text-break"
                            style={{ flex: "0 0 16%" }}
                          >
                            {product.productDescription || (
                              <span className="text-muted">-</span>
                            )}
                          </div>
                          {/* Color */}
                          <div
                            className="text-break"
                            style={{ flex: "0 0 10%" }}
                          >
                            {product.productColor || (
                              <span className="text-muted">-</span>
                            )}
                          </div>
                          {/* Comments -- the big column */}
                          <div style={{ flex: "1 1 auto" }}>
                            {latestProductComment ? (
                              <div className="d-flex align-items-start gap-2">
                                <span>{latestProductComment}</span>
                                {productCommentHistory.length > 0 && (
                                  <button
                                    title="View Comment History"
                                    onClick={() =>
                                      openCommentHistory(
                                        `${product.productStyle || "Product"} Comments`,
                                        product.approvalComments,
                                      )
                                    }
                                    className="btn btn-outline-secondary btn-sm py-0 px-1"
                                  >
                                    <i className="mdi mdi-comment-text-multiple-outline"></i>
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted">No comments</span>
                            )}
                          </div>
                          {/* Status + Reset */}
                          <div
                            className="text-start text-nowrap"
                            style={{ flex: "0 0 260px" }}
                          >
                            <button
                              title="Reset Status"
                              onClick={() =>
                                resetApprovalStatus("product", product.id)
                              }
                              style={{
                                ...ORANGE_BUTTON_STYLE,
                                filter: "none",
                              }}
                              className="btn px-2 rounded-3 py-1 me-1 fs-6 lh-1"
                            >
                              R
                            </button>
                            <span
                              className={`${getApprovalStatusBadge(
                                product.approveStatus,
                              )} fs-6 py-1 px-2 lh-1`}
                              style={getRejectedStatusStyle(
                                product.approveStatus,
                              )}
                            >
                              {formatApprovalStatusLabel(product.approveStatus)}
                            </span>
                            <ApprovedByBadge
                              byType={product?.approvedByType}
                              by={product?.approvedBy}
                              size="lg"
                              className="text-uppercase"
                            />
                          </div>
                          {/* <div className="col-md-2 text-end d-flex justify-content-end align-items-center flex-wrap">
                        {product && approvalData?.approvalExcelFile && (
                          <NamesNumberApproval
                            product={product}
                            refreshFunc={refreshProducts}
                            checkProductsApprovalStatus={
                              checkProductsApprovalStatus
                            }
                          />
                        )}
                      </div>
                      <div className="col-md-3 text-end">
                        {product && (
                          <WoArtworkImgCrud
                            product={product}
                            handleImageDoubleClick={handleImageDoubleClick}
                            refreshFunc={refreshProducts}
                            checkProductsApprovalStatus={
                              checkProductsApprovalStatus
                            }
                          />
                        )}
                      </div> */}
                        </div>
                      </div>
                    );
                  })(),
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-4">
            <div className="text-muted">No products found</div>
          </div>
        )}
      </div>

      {/* Image Preview Modal */}
      <Modal show={showImageModal} onHide={handleCloseModal} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Image Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {selectedImage ? (
            <div className="image-viewer-container">
              <TransformWrapper
                initialScale={1}
                minScale={0.5}
                maxScale={8}
                centerOnInit={true}
                centerZoomedOut={true}
                doubleClick={{ mode: "reset" }}
              >
                {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
                  <div
                    style={{
                      position: "relative",
                    }}
                  >
                    <TransformComponent wrapperClass="" contentClass="">
                      <ImageWIthSignedUrl
                        path={selectedImage.path}
                        alt={selectedImage.alt}
                        imgStyle={{
                          height: "600px",
                          width: "auto",
                          objectFit: "contain",
                        }}
                        imgClasses="zoomable-image bg-light border rounded-3"
                      />
                    </TransformComponent>
                    <div className="zoom-tip mt-2 text-muted d-flex  text-center justify-content-center align-items-center">
                      <h5>Tip: Double-click to reset view</h5>
                      <Controls />
                    </div>
                  </div>
                )}
              </TransformWrapper>
            </div>
          ) : (
            <div className="no-image-placeholder">
              <div className="placeholder-icon">🖼️</div>
              <p>No image available</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={handleCloseModal}>
            Close
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showCommentHistoryModal}
        onHide={() => setShowCommentHistoryModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{commentHistoryTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-light">
          {commentHistoryItems.length > 0 ? (
            <div
              className="d-flex flex-column gap-3"
              style={{ maxHeight: "55vh", overflowY: "auto" }}
            >
              {[...commentHistoryItems].reverse().map((item, idx) => (
                <div key={`${item.timestamp || "no-time"}-${idx}`}>
                  <div className="small text-muted mb-1 px-1">
                    <i className="mdi mdi-clock-outline me-1"></i>
                    {formatHistoryTimestamp(item.timestamp)}
                  </div>
                  <div className="d-flex align-items-start gap-2">
                    <span className="text-secondary" style={{ lineHeight: 1 }}>
                      <i className="mdi mdi-account-circle fs-4"></i>
                    </span>
                    <div
                      className="rounded-3 border px-3 py-2 bg-white"
                      style={{ whiteSpace: "pre-wrap", maxWidth: "95%" }}
                    >
                      {item.comment}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-muted">No comment history available.</div>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)}>
        <Modal.Header className="py-1" closeButton>
          <Modal.Title>Approval Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-1">
          {parsedContactData && (
            <>
              <h5 className="border-bottom border-2 w-fit pe-2">
                Contact Details
              </h5>
              <div className="row">
                <div className="col-6 fw-bold">Name :</div>
                <div className="col-6">
                  {parsedContactData?.firstName} {parsedContactData?.lastName}
                </div>
              </div>
              <div className="row">
                <div className="col-6 fw-bold">Email :</div>
                <div className="col-6">{parsedContactData?.email}</div>
              </div>
              <div className="row">
                <div className="col-6 fw-bold">Phone :</div>
                <div className="col-6">{parsedContactData?.phone}</div>
              </div>
              <hr />
            </>
          )}
          <div>
            <h5 className="border-bottom border-2 w-fit pe-2">
              Approval Details
            </h5>
            <div className="row mb-1">
              <div className="col-6 fw-bold">WA-ID:</div>
              <div className="col-6">
                {approvalData?.approvalId ? `WA-${approvalData.approvalId}` : "-"}
              </div>
            </div>
            <div className="row mb-1">
              <div className="col-6 fw-bold">W-ID:</div>
              <div className="col-6">
                {approvalData?.woId ? `W-${approvalData.woId}` : "-"}
              </div>
            </div>
            <div className="row mb-1">
              <div className="col-6 fw-bold">Sent Time:</div>
              <div className="col-6">
                {approvalData?.approvalSentTime
                  ? `${convertSqlTimestampToDate(
                      approvalData.approvalSentTime,
                    )} - ${convertAttendanceRecordToTime(
                      approvalData.approvalSentTime,
                      false,
                    )}`
                  : "-"}
              </div>
            </div>
            <div className="row mb-1">
              <div className="col-6 fw-bold">Last Opened:</div>
              <div className="col-6">
                {approvalData?.lastOpened
                  ? `${convertSqlTimestampToDate(
                      approvalData.lastOpened,
                    )} - ${convertAttendanceRecordToTime(
                      approvalData.lastOpened,
                      false,
                    )}`
                  : "-"}
              </div>
            </div>
            <div className="row mb-1">
              <div className="col-6 fw-bold">Times Opened:</div>
              <div className="col-6">{approvalData?.timesOpened || "-"}</div>
            </div>
            <div className="row mb-1">
              <div className="col-6 fw-bold">Approve Time:</div>
              <div className="col-6">
                {approvalData?.approveTime
                  ? `${convertSqlTimestampToDate(
                      approvalData.approveTime,
                    )} - ${convertAttendanceRecordToTime(
                      approvalData.approveTime,
                      false,
                    )}`
                  : "-"}
              </div>
            </div>
            <div className="row mb-1">
              <div className="col-6 fw-bold">Approved By:</div>
              <div className="col-6">{approvedByLabel}</div>
            </div>
          </div>
          <div className="text-end mt-2 border-top pt-2">
            <Button
              variant="secondary"
              onClick={() => setShowDetailsModal(false)}
            >
              Close
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
